import { useState } from "react";
import { Package, Recycle, ShoppingBag, Wrench, X, CheckCircle2 } from "lucide-react";

const services = [
  {
    icon: Package,
    title: "Circular Economy Support",
    desc: "Providing complete product lifecycle management, ensuring maximum resource recovery and longer product utilisation.",
    features: [
  { label: "Product Lifecycle Management", text: "Comprehensive documentation including maintenance protocols and repair instructions" },
  { label: "End-of-Life Guidance", text: "Detailed recycling procedures, disassembly instructions, and environmentally responsible disposal pathways" },
  { label: "Optimized Resource Recovery", text: "Complete material specifications and disassembly guides facilitating efficient recycling processes" },
  { label: "Comprehensive Material Documentation", text: "Detailed component specifications, raw material data, and chemical composition records" },
],
  },
  {
    icon: Recycle,
    title: "Environmental Impact Mitigation",
    desc: "Guaranteeing sustainable outcomes by monitoring carbon emissions, optimizing resource efficiency.",
    features: [
  { label: "Waste Reduction", text: "Strategic end-of-life protocols preventing landfill accumulation and promoting circular recovery" },
  { label: "Carbon Footprint Reduction", text: "Enhanced product reuse minimizing virgin material extraction and manufacturing emissions" },
  { label: "Resource Optimization", text: "Efficient material and energy utilization throughout the production process" },
  { label: "Environmental Performance Tracking", text: "Comprehensive emissions data, resource consumption metrics, and sustainability indicators" },
],
  },
  {
    icon: ShoppingBag,
    title: "Sustainable Production Practices",
    desc: "Responsible manufacturing through transparent, durable product design and rigorous tracking of raw material origins.",
    features: [
  { label: "Quality-Driven Design", text: "Transparency incentivizing manufacturers to develop durable, environmentally conscious products" },
  { label: "Responsible Material Sourcing", text: "Verified documentation of recycled content, material provenance, and hazardous substance elimination" },
  { label: "Lifecycle Impact Monitoring", text: " Real-time tracking of carbon emissions, water usage, and environmental impact across product lifecycles" },
],
  },
  {
    icon: Wrench,
    title: "Consumer Transparency & Engagement",
    desc: "Provide transparent, verifiable environmental metrics, enabling consumers to confidently assess sustainability claims and make responsible purchases.",
    features: [
  { label: "Data-Driven Decision Making", text: "Access to comparative environmental impact data supporting informed, sustainable purchasing choices" },
  { label: "Verified Sustainability Claims", text: "Auditable data preventing misleading environmental marketing and greenwashing practices" },
  { label: "Circular Consumption Promotion", text: "Encouraging responsible purchasing behaviors and circular economy participation." },
],
  },
];

function ServiceModal({ service, onClose }) {
  if (!service) return null;
  const Icon = service.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
        style={{ animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1) both" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Blue banner */}
        <div
          className="relative px-7 pt-7 pb-8"
          style={{ background: "linear-gradient(135deg, #2563eb 0%, #4f8ef7 100%)" }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            style={{ background: "rgba(255,255,255,0.2)" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.35)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
          >
            <X size={16} color="white" />
          </button>

          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
            style={{ background: "rgba(255,255,255,0.18)" }}
          >
            <Icon size={24} color="white" />
          </div>

          <h3 className="text-white text-xl font-bold mb-2">{service.title}</h3>
          <p className="text-blue-100 text-sm leading-relaxed">{service.desc}</p>
        </div>

        {/* Body */}
        <div className="px-7 py-6">
          <h4 className="text-gray-900 font-bold text-base mb-4">Key Features</h4>
          <ul className="space-y-3 mb-7">
            {service.features.map((f, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-blue-500 mt-0.5 shrink-0" />
                <span className="text-gray-600 text-sm leading-relaxed">
  <span className="font-semibold text-gray-800">{f.label}: </span>
  {f.text}
</span>
              </li>
            ))}
          </ul>

          <button
            onClick={onClose}
            className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-opacity duration-200 hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #2563eb 0%, #4f8ef7 100%)" }}
          >
            Got it, thanks!
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.88) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

function CircularityServices() {
  const [activeService, setActiveService] = useState(null);

  return (
    <section id="circularity" className="py-24 bg-white relative overflow-hidden">

      {/* Background circles */}
      <div className="absolute w-[500px] h-[500px] border border-blue-100 rounded-full top-10 right-[-100px] opacity-50 pointer-events-none" />
      <div className="absolute w-[600px] h-[600px] border border-blue-100 rounded-full bottom-[-200px] left-[-200px] opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 text-center">

        <span className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium">
          Circular Economy
        </span>

        <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-5">
          Circularity Services
        </h2>

        <p className="text-gray-500 max-w-3xl mx-auto mb-16 text-lg leading-relaxed">
          Transcend EU DPP compliance by leveraging circular services as a strategic tool for value creation and broader market reach. Our secure, connected platform facilitates seamless engagement between brands and end-users.
        </p>

        <div className="grid md:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                onClick={() => setActiveService(service)}
                className="group cursor-pointer rounded-2xl border-2 p-6 flex flex-col items-center transition-all duration-300"
                style={{ borderColor: "transparent" }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "#2563eb";
                  e.currentTarget.style.boxShadow = "0 8px 32px rgba(37,99,235,0.10)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "transparent";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-blue-100 flex items-center justify-center transition-all duration-300 group-hover:bg-blue-600 group-hover:shadow-lg group-hover:scale-110">
                  <Icon className="text-blue-600 group-hover:text-white transition-colors duration-300" size={28} />
                </div>

                <h3 className="text-lg font-semibold mb-3 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h3>

                <p className="text-gray-500 text-sm leading-relaxed flex-1">
                  {service.desc}
                </p>

                {/* Learn more — slides in on hover */}
                <div className="mt-4 h-6 overflow-hidden">
                  <span className="block translate-y-6 group-hover:translate-y-0 transition-transform duration-300 text-blue-600 text-sm font-semibold flex items-center gap-1 justify-center">
                    Learn more →
                  </span>
                </div>
              </div>
            );
          })}
        </div>
{/* 
        <div className="mt-16">
          {/* <button className="bg-blue-600 text-white px-10 py-4 rounded-xl shadow-lg hover:bg-blue-700 transition hover:scale-105 duration-300 font-semibold">
            Enable Your Connected Product Ecosystem
          </button> */}
        {/* </div> */} 

      </div>

      {/* Modal */}
      {activeService && (
        <ServiceModal service={activeService} onClose={() => setActiveService(null)} />
      )}
    </section>
  );
}

export default CircularityServices;